import React from 'react';

import { graphql } from 'gatsby';
import { Box, Heading, Image, Text } from '@lucid/components-react';
import ReactHtmlParser from 'react-html-parser';
import {
  Drupal_ParagraphSeoImage,
  Drupal_ParagraphFlexiSection
} from 'generated/drupal-graphql';
import { getProcessedValue } from '@lucid/utils';
import { theme, LucidTheme } from '@lucid/theme';

const FlexiLocationsSection = ({
  entity: { fieldFlexiLocationImage, fieldLocations }
}: {
  entity: {
    fieldFlexiLocationImage: { entity: Drupal_ParagraphSeoImage };
    fieldLocations: { entity: Drupal_ParagraphFlexiSection }[];
  };
}) => {
  return (
    <Box
      css={(theme: LucidTheme) => ({
        label: 'flexiLocationsSection',
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        width: '100%',
        marginTop: theme.space[6],
        marginBottom: theme.space[6]
      })}
      gridColumn="1 / -1"
    >
      <Box
        css={(theme: LucidTheme) => ({
          label: 'content',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingLeft: theme.space[6],
          paddingRight: theme.space[6],
          marginBottom: theme.space[4],
          width: '100%'
        })}
        flexDirection={['column', 'column', 'column', 'row', 'row']}
      >
        <Box
          px={[0, 0, 0, 4]}
          width={['100%', '100%', '100%', 'calc(100% * 7/12)']}
        >
          <Image
            imageAlt={fieldFlexiLocationImage?.entity?.fieldAltText || ''}
            imageSrc={fieldFlexiLocationImage?.entity?.fieldImageLink || ''}
            loading="lazy"
          />
        </Box>
        <Box width={['100%', '100%', '100%', 'calc(100% * 5/12)']}>
          <Box
            display="grid"
            gridGap={theme.space[6]}
            gridTemplateColumns={['1fr', '1fr', '1fr 1fr', '1fr 1fr']}
          >
            {fieldLocations?.map(
              ({
                entity: {
                  fieldFlexiHeader,
                  fieldFlexiSubheader,
                  fieldFlexiBody
                }
              }) => {
                return (
                  <Box key={fieldFlexiHeader} as="article">
                    {fieldFlexiHeader && (
                      <Heading as="h2" my="3" variant="headlineM">
                        {fieldFlexiHeader}
                      </Heading>
                    )}
                    {fieldFlexiSubheader && (
                      <Box my="3">
                        <Text variant="labelS400">
                          {fieldFlexiSubheader}
                        </Text>
                      </Box>
                    )}
                    {fieldFlexiBody && (
                      <Box my="3">
                        <Text as="div" variant="labelXS400">
                          {ReactHtmlParser(getProcessedValue(fieldFlexiBody))}
                        </Text>
                      </Box>
                    )}
                  </Box>
                );
              }
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default FlexiLocationsSection;

export const query = graphql`
  fragment flexiLocationsSection on Drupal_ParagraphFlexiLocationsSection {
    fieldFlexiLocationImage {
      entity {
        ... on Drupal_ParagraphSeoImage {
          fieldAltText
          fieldImageLink
        }
      }
    }
    fieldLocations {
      entity {
        ... on Drupal_ParagraphFlexiSection {
          fieldFlexiHeader
          fieldFlexiSubheader
          fieldFlexiBody {
            processed
          }
        }
      }
    }
  }
`;
